import { ReactElement, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import SessionForm from './SessionForm';
import TabMenu from '../../../components/TabMenu';
import {
  HiOutlineChartBarSquare,
  HiOutlineCalculator,
  HiOutlineListBullet,
  HiOutlineMagnifyingGlass,
} from 'react-icons/hi2';
import { SessionAnalysisType } from '../../../types/SessionAnalysisType';
import { useRidersById } from '../../../hooks/useRiders';
import { parse, isValid } from 'date-fns';
import { useSessionColor, getSessionColorHex, createColoredFavicon, setFavicon } from '../../../hooks/useSessionColor';

interface SessionDetailsProps {
  sessionId: string;
  session: any;                 // phase-1 summary — drives title/form/tabs/list
  isSessionPending: boolean;    // summary pending
  isSessionFetching: boolean;   // summary fetching
  fullSession?: any;            // phase-2 full payload — drives plot tabs
  isFullPending?: boolean;
  isFullFetching?: boolean;
  isNew: boolean;
  refetchSession: () => Promise<void>;
}

export default function SessionDetails({
  sessionId,
  session,
  isSessionPending,
  isSessionFetching,
  fullSession,
  isFullPending,
  isFullFetching,
  isNew,
  refetchSession,
}: SessionDetailsProps): ReactElement {
  const { data: ridersById } = useRidersById();
  const [sessionColor] = useSessionColor(sessionId);
  const colorHex = getSessionColorHex(sessionColor);

  // Store the original favicon to restore on unmount
  const originalFaviconRef = useRef<string | null>(null);

  // Capture the original favicon on mount
  useEffect(() => {
    const link = document.querySelector('link[rel*=\'icon\']') as HTMLLinkElement | null;
    originalFaviconRef.current = link?.href || '/favicon.ico';
  }, []);

  // Update browser tab title with rider name and date
  useEffect(() => {
    if (session && !isNew) {
      const riderName = ridersById?.get(session.rider_id)?.nickname || 'Unknown Rider';
      let dateStr = '';
      if (session.date) {
        const parsedDate = parse(session.date, 'yyyy-MM-dd', new Date());
        if (isValid(parsedDate)) {
          dateStr = parsedDate.toLocaleDateString();
        }
      }
      document.title = dateStr ? `${riderName} - ${dateStr}` : riderName;
    } else if (isNew) {
      document.title = 'New Session';
    }

    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = 'Cirrus';
    };
  }, [session, ridersById, isNew, sessionColor]);

  // Update favicon based on session color
  useEffect(() => {
    const coloredFavicon = createColoredFavicon(sessionColor);
    setFavicon(coloredFavicon, originalFaviconRef.current || undefined);

    // Cleanup: restore original favicon when component unmounts
    return () => {
      setFavicon(null, originalFaviconRef.current || undefined);
    };
  }, [sessionColor]);

  // Check if Tyre Plots should be shown based on analysis_type
  const showTyrePlots =
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_REFERENCE ||
    session?.analysis_type === SessionAnalysisType.TRAVERSE_TYRE_COMPARISON;

  // Build tabs array conditionally
  const tabs = [
    { path: 'config', label: 'Plan', icon: <HiOutlineListBullet className={'cr-size-7'} /> },
    { path: 'results', label: 'Data Table', icon: <HiOutlineCalculator className={'cr-size-7'} /> },
    { path: 'charts', label: 'Aero Report', icon: <HiOutlineChartBarSquare className={'cr-size-7'} /> },
  ];

  // Add Tyre Report tab only if conditions are met
  if (showTyrePlots) {
    tabs.push({ path: 'tyre-plots', label: 'Tyre Report', icon: <HiOutlineChartBarSquare className={'cr-size-7'} /> });
  }

  // Show Breakdown tab once the session has at least one run (runs are nested under each config)
  const showBreakdown = !!session?.configs?.some((c: any) => (c?.runs?.length ?? 0) > 0);
  if (showBreakdown) {
    tabs.push({ path: 'breakdown', label: 'Calculation', icon: <HiOutlineMagnifyingGlass className={'cr-size-7'} /> });
  }

  return (
    <div className="cr-relative cr-w-full">
      {/* Vertical color stripes on left and right sides */}
      {sessionColor && (
        <>
          <div
            className="cr-fixed cr-left-0 cr-top-0 cr-h-full cr-w-1 cr-z-50"
            style={{ backgroundColor: colorHex }}
          />
          <div
            className="cr-fixed cr-right-0 cr-top-0 cr-h-full cr-w-1 cr-z-50"
            style={{ backgroundColor: colorHex }}
          />
        </>
      )}
      <SessionForm
        sessionId={sessionId}
        session={session}
        isSessionFetching={isSessionFetching}
        isSessionPending={isSessionPending}
        isNew={isNew}
      />
      {session?.configs && session.configs.length > 0 && (
        <>
          <TabMenu tabs={tabs} basePath={`/sessions/${sessionId}`} className="cr-mb-0" />
          <Outlet context={{
            sessionId,
            // Phase-1 summary for the config/run list...
            summary: session,
            isSummaryFetching: isSessionFetching,
            isSummaryPending: isSessionPending,
            // ...and the phase-2 full payload for plot tabs. `session` /
            // isSession* stay bound to the FULL query so plot tabs need no rename.
            session: fullSession,
            isSessionFetching: isFullFetching ?? isSessionFetching,
            isSessionPending: isFullPending ?? isSessionPending,
            isFullFetching: isFullFetching ?? isSessionFetching,
            isFullPending: isFullPending ?? isSessionPending,
            isNew,
            refetchSession,
          }} />
        </>
      )}
    </div>
  );
}
